import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import messages from "../../../../assets/locale/messages";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { UIButton } from "../../../../components/ui";
import { useParams } from "react-router-dom";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import Table from "../../../../components/Table";
import { TableCell, TableRow } from "@mui/material";
import {
  CloseCircleIcon,
  TickCircleIcon,
  TickIcon,
} from "../../../../utils/Icons";
import { editRoleRequest, getRoleRequest } from "../../../../store/Role/actions";
import EmptyState from "../../../../components/EmptyState";
import rolePlaceholder from "../../../../assets/images/request-screen/request-placeholder.svg";
import Checkbox from "../../../../components/Checkbox";

const ViewRole = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loader);
  const lang = useSelector((state) => state.locale.lang);
  const { roleDetail: list } = useSelector((state) => state.role);
  const { roles, shared } = messages[lang];

  const [isRoleEdit, setRoleEdit] = useState(false);
  const [rolePermissions, setRolePermissions] = useState([
    {
      can_edit: null,
      can_add: null,
      can_delete: null,
      can_view: null,
      module_name: "",
    },
  ]);

  useEffect(() => {
    dispatch(getRoleRequest({ id }));
  }, [lang, id, dispatch]);

  useEffect(() => {
    setRolePermissions(list?.permissions);
  }, [list]);

  const handleUpdatePermissions = (value, permission, action) => {
    const updatedPermission = { ...permission };
    switch (action) {
      case "can_view":
        updatedPermission.can_view = value;
        break;
      case "can_add":
        updatedPermission.can_add = value;
        break;
      case "can_edit":
        updatedPermission.can_edit = value;
        break;
      case "can_delete":
        updatedPermission.can_delete = value;
        break;
      default:
        break;
    }

    setRolePermissions((prevPermissions) => {
      const permissionIndex = prevPermissions.findIndex(
        (perm) => perm.module_name === permission.module_name
      );

      if (permissionIndex > -1) {
        const newPermissions = [...prevPermissions];
        newPermissions[permissionIndex][action] = updatedPermission[action];
        return newPermissions;
      } else {
        return [...prevPermissions, updatedPermission];
      }
    });
  };

  const handleRowUpdate = (value, name) => {
    setRolePermissions((prevState) => {
      const updatedState = [...prevState];
      const currentState = updatedState.findIndex(
        (per) => per.module_name === name
      );
      if (currentState !== -1) {
        updatedState[currentState] = {
          ...updatedState[currentState],
          can_view: value,
          can_edit: value,
          can_delete: value,
          can_add: value,
        };
      }
      return updatedState;
    });
  };

  const handleBulkPermissionUpdate = () => {
    dispatch(editRoleRequest({ role_id: id, permissions: rolePermissions }));
    setRoleEdit(false);
  };

  const renderViewRolesList = () => {
    if (!isLoading && ![list][0]?.permissions?.length) {
      return (
        <>
          <div className="bg-white rounded-4 mt-4 pb-3">
            <EmptyState
              description={roles.listRoles.placeholder.noRoleFound}
              imgSrc={rolePlaceholder}
              showActionButton={false}
            />
          </div>
        </>
      );
    } else {
      return (
        <div className="bg-white rounded-4 mt-2 mb-5 pb-3">
          <Table
            className="rounded-4"
            headlines={[
              roles.listRoles.role,
              roles.listRoles.modules,
              shared.view,
              shared.add,
              shared.edit,
              shared.delete,
            ]}
            hasActions={false}
            hasSizeLimitation={false}
            rows={[list].map((row, index) => (
              <React.Fragment key={index}>
                {rolePermissions?.map((permission, idx) => (
                  <TableRow key={idx}>
                    {idx === 0 && (
                      <TableCell
                        className="pointer"
                        rowSpan={row.permissions?.length}
                        style={{ verticalAlign: "middle" }}
                      >
                        {row.name ?? row.name_en ?? "-"}
                      </TableCell>
                    )}
                    <TableCell className="pointer">
                      <div className={`d-flex align-items-center gap-2`}>
                        {isRoleEdit ? (
                          <Checkbox
                            checked={
                              permission.can_view &&
                              permission.can_edit &&
                              permission.can_add &&
                              permission.can_delete
                            }
                            onChange={(value) =>
                              handleRowUpdate(value, permission.module_name)
                            }
                            name={permission?.module_name}
                          />
                        ) : permission.can_view ||
                          permission.can_edit ||
                          permission.can_add ||
                          permission.can_delete ? (
                          <TickIcon />
                        ) : (
                          <CloseCircleIcon />
                        )}
                        {permission.module_name}
                      </div>
                    </TableCell>
                    <TableCell className="pointer">
                      {isRoleEdit ? (
                        <Checkbox
                          checked={permission?.can_view}
                          onChange={(value) =>
                            handleUpdatePermissions(
                              value,
                              permission,
                              "can_view"
                            )
                          }
                          name={`can_view-${index}-${idx}`}
                        />
                      ) : permission.can_view ? (
                        <TickCircleIcon />
                      ) : (
                        <CloseCircleIcon />
                      )}
                    </TableCell>
                    <TableCell className="pointer">
                      {isRoleEdit ? (
                        <Checkbox
                          checked={permission?.can_add}
                          onChange={(value) =>
                            handleUpdatePermissions(
                              value,
                              permission,
                              "can_add"
                            )
                          }
                          name={`can_add-${index}-${idx}`}
                        />
                      ) : permission.can_add ? (
                        <TickCircleIcon />
                      ) : (
                        <CloseCircleIcon color={"#FF7F7F"} />
                      )}
                    </TableCell>
                    <TableCell className="pointer">
                      {isRoleEdit ? (
                        <Checkbox
                          checked={permission?.can_edit}
                          onChange={(value) =>
                            handleUpdatePermissions(
                              value,
                              permission,
                              "can_edit"
                            )
                          }
                          name={`can_edit-${index}-${idx}`}
                        />
                      ) : permission.can_edit ? (
                        <TickCircleIcon />
                      ) : (
                        <CloseCircleIcon color={"#FF7F7F"} />
                      )}
                    </TableCell>
                    <TableCell className="pointer">
                      {isRoleEdit ? (
                        <Checkbox
                          checked={permission?.can_delete}
                          onChange={(value) =>
                            handleUpdatePermissions(
                              value,
                              permission,
                              "can_delete"
                            )
                          }
                          name={`can_delete-${index}-${idx}`}
                        />
                      ) : permission.can_delete ? (
                        <TickCircleIcon />
                      ) : (
                        <CloseCircleIcon color={"#FF7F7F"} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          />
        </div>
      );
    }
  };
  return (
    <div className={`conatiner requests-list-container`}>
      <div className="events-list-container">
        <div className="page-title-margin">
          <Breadcrumbs
            list={[
              {
                path: ROUTE_PATHS.roleManagement,
                label: roles.listBreadCrumbs.roleManagement,
              },
              {
                path: "",
                label: roles.listBreadCrumbs.roleEdit,
              },
            ]}
            BreadCrumbHasBackIcon={false}
          />
        </div>
        <Box className="d-flex align-items-center mb-4 flex-md-nowrap flex-wrap justify-content-between w-100">
          <Typography variant="h5">{list?.name ?? list?.name_en}</Typography>
          {!isRoleEdit && (
            <UIButton
              appearance="gradient"
              type="button"
              onClick={() => setRoleEdit(true)}
            >
              {roles.listRoles.editPermissions}
            </UIButton>
          )}
        </Box>
        <div className="d-flex flex-column justify-content-between">
          {renderViewRolesList()}
          {isRoleEdit && (
            <Box className="d-flex align-items-center gap-3 view-role-page__actions">
              <UIButton
                appearance="gradient"
                type="button"
                onClick={() => handleBulkPermissionUpdate()}
              >
                {roles.listRoles.updatePermissions}
              </UIButton>
              <UIButton
                variant="outlined"
                color="inherit"
                type="button"
                onClick={() => setRoleEdit(false)}
              >
                {shared.cancel}
              </UIButton>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};
export default ViewRole;
